import React from 'react';
import { Clock, Calculator, Edit2, Trash2 } from 'lucide-react';
import { MealData } from './QuickMealLogger';

interface MealLogEntryProps {
  meal: MealData & { id: string; timestamp: string };
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export const MealLogEntry: React.FC<MealLogEntryProps> = ({ meal, onEdit, onDelete }) => {
  const getMealTypeIcon = (type: string) => {
    switch (type) {
      case 'breakfast': return '🍳';
      case 'lunch': return '🥗';
      case 'dinner': return '🍽️';
      case 'snack': return '🍎';
      default: return '🍽️';
    }
  };

  const getMealTypeColor = (type: string) => {
    switch (type) {
      case 'breakfast': return 'bg-yellow-100 text-yellow-800';
      case 'lunch': return 'bg-green-100 text-green-800';
      case 'dinner': return 'bg-blue-100 text-blue-800';
      case 'snack': return 'bg-orange-100 text-orange-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const formatTime = (time: string) => {
    return time;
  };

  const totalCalories = meal.calories;
  const carbsPercent = Math.round((meal.carbs * 4 / totalCalories) * 100) || 0;
  const proteinPercent = Math.round((meal.protein * 4 / totalCalories) * 100) || 0;
  const fatPercent = Math.round((meal.fat * 9 / totalCalories) * 100) || 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 md:p-4 hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start space-x-3 mb-3 sm:mb-0">
          <div className="p-2 md:p-3 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl text-xl md:text-2xl">
            {getMealTypeIcon(meal.mealType)}
          </div>
          
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-medium text-slate-800 text-sm md:text-base">{meal.name}</h3>
              <span className={`px-2 py-0.5 text-xs rounded-full ${getMealTypeColor(meal.mealType)}`}>
                {meal.mealType.charAt(0).toUpperCase() + meal.mealType.slice(1)}
              </span>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-slate-600 mb-2">
              <div className="flex items-center space-x-1">
                <Clock className="h-3.5 w-3.5" />
                <span>{formatTime(meal.time)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calculator className="h-3.5 w-3.5" />
                <span>{meal.calories} calories</span>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>{meal.carbs}g carbs ({carbsPercent}%)</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span>{meal.protein}g protein ({proteinPercent}%)</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span>{meal.fat}g fat ({fatPercent}%)</span>
              </div>
            </div>
            
            {meal.notes && (
              <p className="mt-2 text-xs text-slate-500 italic">{meal.notes}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-1 self-end sm:self-start">
          <button 
            onClick={() => onEdit(meal.id)}
            className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button 
            onClick={() => onDelete(meal.id)}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
